/**
 * Custom-domain client portals — service hooks.
 *
 * Two surfaces:
 *
 *   1. The studio-side Settings page (CRUD on their own custom_domains
 *      row): `useCustomDomain`, `useRegisterCustomDomain`,
 *      `useVerifyCustomDomain`, `useDeleteCustomDomain`.
 *
 *   2. The public portal pages (rendered when a client visits via the
 *      studio's custom hostname): `useCurrentCustomDomain` reads the
 *      browser's `window.location.hostname`, looks it up in the
 *      public view, and returns the studio's brand snippets so the
 *      page can recolor/relogo itself.
 *
 * The public hook is anon — it queries `custom_domains_public` which
 * exposes only display fields (no DNS state, no internal ids).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { isOrbitOwnedHost } from '@/lib/hostRouting';

// ─── Types ───────────────────────────────────────────────────────

/**
 * P1-1 · `live` is a new state and it is the only one that means the
 * domain works.
 *
 *   pending_dns  DNS is missing or points somewhere else.
 *   verified     DNS is correct. Orbit has NOT registered the hostname
 *                on its hosting project yet, so nothing serves it and
 *                no certificate exists. NOT public — share links keep
 *                using the Orbit domain.
 *   live         custom-domain-verify fetched https://<hostname>/ and
 *                Orbit answered. Public, and share links switch over.
 *
 * `custom_domains_public` (and therefore src/lib/shareUrl.ts) exposes
 * `live` rows only — see migration 20260812120000.
 */
export type CustomDomainStatus =
  | 'pending_dns'
  | 'verified'
  | 'live'
  | 'failed'
  | 'disabled';

export interface CustomDomain {
  id: string;
  org_id: string;
  hostname: string;
  status: CustomDomainStatus;
  cname_target: string;
  verified_at: string | null;
  // ── P1-1 (20260812120000) — the HTTPS probe's evidence ──────────
  /** When custom-domain-verify last tried to actually LOAD the host. */
  last_https_check_at: string | null;
  /** One human sentence about what happened on that request. */
  last_https_detail: string | null;
  brand_logo_url: string | null;
  // Wave 6.1 — LONG column names after
  // 20260720500000_wave6_1_custom_domain_column_reconcile.sql
  // renamed the SHORT variants. Nulls before a studio sets them.
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  brand_bg_color: string | null;
  brand_fg_color: string | null;
  brand_font_family: string | null;
  hide_orbit_footer: boolean;
}

/** Public-facing slice — what the portal pages read by hostname. */
export interface PublicCustomDomain {
  hostname: string;
  org_id: string;
  brand_logo_url: string | null;
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  // Wave 6.1 — bg + fg were exposed in the studio Settings UI
  // (background + foreground brand colors) but never made it to
  // the anon view. Now they do.
  brand_bg_color: string | null;
  brand_fg_color: string | null;
  brand_font_family: string | null;
  hide_orbit_footer: boolean;
}

// ─── Studio-side hooks ──────────────────────────────────────────

export const useCustomDomain = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['custom-domain', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('custom_domains')
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as CustomDomain | null;
    },
    enabled: !!orgId,
  });
};

/**
 * LAPSE-1 — is this org's domain allowed to serve right now, and if the
 * plan is gone, when does the 14-day grace run out? Mirrors the
 * predicate on `custom_domains_public` (org_custom_domain_serving) so
 * Settings can say the same thing the router is doing.
 */
export interface CustomDomainState {
  serving: boolean;
  on_tier: boolean;
  ended_at: string | null;
  cutoff_at: string | null;
}

export const useCustomDomainState = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['custom-domain-state', orgId],
    queryFn: async (): Promise<CustomDomainState | null> => {
      if (!orgId) return null;
      const { data, error } = await (supabase as any).rpc('org_custom_domain_state', { p_org_id: orgId });
      // The RPC arrives with the LAPSE-1 migration; before it lands,
      // behave as if the domain serves — the view does too.
      if (error) return null;
      return (data ?? null) as CustomDomainState | null;
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
};

const callFunction = async <T,>(body: Record<string, unknown>): Promise<T> => {
  const { data: { session } } = await supabase.auth.getSession();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/custom-domain-verify`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const e = new Error(err.error ?? `Failed (${resp.status})`) as Error & { status?: number; reason?: string };
    e.status = resp.status;
    e.reason = err.reason;
    throw e;
  }
  return resp.json();
};

export const useRegisterCustomDomain = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hostname: string) =>
      callFunction<{ ok: boolean; hostname: string; cname_target: string }>({
        op: 'register', hostname,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-domain'] }),
  });
};

/**
 * Re-run the check. P1-1 widened the response: `verified` still means
 * "DNS resolves the way we asked", `live` is the separate, stronger
 * claim that we fetched the hostname over HTTPS and Orbit answered.
 * Callers that want to tell a studio their domain WORKS must read
 * `live` — `verified` on its own is a half-finished connection.
 */
export interface VerifyCustomDomainResult {
  status: CustomDomainStatus;
  /** DNS is right OR the host serves Orbit. Kept for older callers. */
  verified: boolean;
  /** The hostname served Orbit over HTTPS. This is the real one. */
  live: boolean;
  dns_ok: boolean;
  resolved_to: string | null;
  a_records: string[] | null;
  error: string | null;
  expected: string;
  https_detail: string;
  /** One sentence on what to do next. Null when the domain is live. */
  hint: string | null;
}

export const useVerifyCustomDomain = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      callFunction<VerifyCustomDomainResult>({
        op: 'verify',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-domain'] }),
  });
};

export const useDeleteCustomDomain = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => callFunction<{ ok: boolean }>({ op: 'delete' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-domain'] }),
  });
};

/// Update brand settings without re-registering the domain. Studios
/// tweak logo/colors after their domain is verified.
export const useUpdateCustomDomainBranding = () => {
  const { data: orgId } = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Pick<CustomDomain,
      'brand_logo_url' | 'brand_primary_color' | 'brand_accent_color'
      | 'brand_bg_color' | 'brand_fg_color' | 'brand_font_family'
      | 'hide_orbit_footer'
    >>) => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const { data, error } = await supabase
        .from('custom_domains')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('org_id', orgId)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as CustomDomain;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-domain'] }),
  });
};

// ─── Public portal hook ─────────────────────────────────────────

/**
 * Look up the hostname the visitor came in through. Returns null when
 * we're on Orbit's main domain (no custom branding needed) or when no
 * LIVE domain matches — the view stopped exposing merely-'verified'
 * rows in P1-1, because DNS resolving is not the same as the hostname
 * loading.
 *
 * Used at the top of public pages (`SignDocument`, `PublicClientPortal`,
 * delivery galleries) to swap logo/colors. Cached per session — host
 * doesn't change between page loads.
 */
export const useCurrentCustomDomain = () => {
  return useQuery({
    queryKey: ['current-custom-domain'],
    queryFn: async () => {
      if (typeof window === 'undefined') return null;
      const host = window.location.hostname;
      // Skip lookups for Orbit-owned hosts (preview URLs, primary
      // domains, localhost) — saves a DB hit on 99% of public traffic.
      if (isOrbitOwnedHost(host)) return null;
      const { data, error } = await (supabase as any)
        .from('custom_domains_public')
        .select('*')
        .eq('hostname', host)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as PublicCustomDomain | null;
    },
    // Static for this page load — no point refetching while the
    // visitor sits on a single page.
    staleTime: Infinity,
  });
};

/**
 * CSS-vars patch the branding hook hands to a portal page wrapper.
 * Apply with: <div style={brandToCSSVars(domain)}>...</div>
 *
 * The portal pages key off Tailwind tokens (--orbit-orange, etc.),
 * so overriding the same vars at the page root rebrands every
 * descendant CTA without touching component code.
 */
export function brandToCSSVars(domain: PublicCustomDomain | null | undefined): React.CSSProperties {
  if (!domain) return {};
  const vars: Record<string, string> = {};
  if (domain.brand_primary_color) {
    vars['--orbit-orange'] = hexToHsl(domain.brand_primary_color) ?? domain.brand_primary_color;
  }
  if (domain.brand_accent_color) {
    vars['--lunar-teal'] = hexToHsl(domain.brand_accent_color) ?? domain.brand_accent_color;
  }
  // Wave 6.1 — bg + fg now flow through so the studio's chosen
  // background / foreground actually paint on the portal instead
  // of silently defaulting to Orbit's palette.
  if (domain.brand_bg_color) {
    vars['--background'] = hexToHsl(domain.brand_bg_color) ?? domain.brand_bg_color;
  }
  if (domain.brand_fg_color) {
    vars['--foreground'] = hexToHsl(domain.brand_fg_color) ?? domain.brand_fg_color;
  }
  if (domain.brand_font_family) {
    vars['--font-display'] = domain.brand_font_family;
  }
  return vars as React.CSSProperties;
}

/// Convert "#275FBE" into Tailwind's HSL space ("218 66% 45%") so the
/// override matches the format index.css uses for --orbit-blue etc.
/// (Example updated in BRAND-1 — the old one quoted orbit-orange.)
function hexToHsl(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
